
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { supabase, ConsentData } from '@/integrations/supabase/client';
import { Badge } from '@/components/ui/badge';
import { Loader2 } from 'lucide-react';

export const TermsPrivacyConsents = () => {
  const { data: consents = [], isLoading, error } = useQuery({
    queryKey: ['termsPrivacyConsents'],
    queryFn: async () => {
      try {
        // Fetch consents directly from the table
        const { data: consentsData, error: consentsError } = await supabase
          .from('terms_privacy_consents')
          .select('*')
          .order('created_at', { ascending: false });
        
        if (consentsError) throw consentsError;
        
        // Fetch user info to display alongside consents
        const enhancedConsents = await Promise.all(
          (consentsData || []).map(async (consent: any) => {
            const { data: userData } = await supabase
              .from('profiles')
              .select('username, email')
              .eq('id', consent.user_id)
              .single();
              
            return {
              ...consent,
              username: userData?.username,
              email: userData?.email
            };
          })
        );
        
        return enhancedConsents as ConsentData[];
      } catch (error) {
        console.error('Error fetching terms & privacy consents:', error);
        throw error;
      }
    }
  });

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Terms & Privacy Consents</CardTitle>
          <CardDescription>Loading consent data...</CardDescription>
        </CardHeader>
        <CardContent className="flex justify-center py-8">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Terms & Privacy Consents</CardTitle>
          <CardDescription>Error loading consent data</CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-destructive">An error occurred while loading the data</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Terms & Privacy Consents</CardTitle>
        <CardDescription>
          View all users who have accepted terms and privacy policy
        </CardDescription>
      </CardHeader>
      <CardContent>
        {consents.length === 0 ? (
          <p className="text-center py-4 text-gray-500">No consent data available</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Terms Status</TableHead>
                <TableHead>Privacy Status</TableHead>
                <TableHead>IP Address</TableHead>
                <TableHead>Date</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {consents.map((consent) => (
                <TableRow key={consent.id}>
                  <TableCell className="font-medium">
                    {consent.username || consent.email || 'Unknown User'}
                    <div className="text-xs text-gray-500">{consent.user_id}</div>
                  </TableCell>
                  <TableCell>
                    <Badge className={consent.terms_accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {consent.terms_accepted ? 'Accepted' : 'Declined'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge className={consent.privacy_accepted ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {consent.privacy_accepted ? 'Accepted' : 'Declined'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    {consent.ip_address || 'Not recorded'}
                  </TableCell>
                  <TableCell>
                    {new Date(consent.created_at || '').toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

export default TermsPrivacyConsents;
